import { useRouter } from 'next/router'

// Libs
import { useFollowers, useFollowingMembers } from '@/hooks/livemallclub/useFollow'
import { useMemberData } from '@/hooks/useMemberData'
import { useMemberRoleInfo } from '@/hooks/useMemberRoleInfo'
import { useUserProfile } from '@/hooks/useUserProfile'

// Constants
import { KolType, MerchantType } from '@/api/fetchKOLOrMerchantInfo'
import { ROLE_TYPE } from '@/constants/roleType'
import { TUserProfile } from '@/types/user-profile'
import { getLiveMallClubRole } from '@/utils/live-mall-club/role'

interface IUserProfileDataParams {
  memberInfoId?: string
}
export type TProfileData = {
  name?: string
  intro?: string | null
  merchantId: string
  userProfile: TUserProfile
  coverPhotoUrl?: string | null
  headshotUrl: string | null
  roles: number[]
  isMyProfile: boolean
  profileType: (typeof ProfileType)[keyof typeof ProfileType] | null
  followingMembers: TUserProfile[]
  followers: TUserProfile[]
  isClubMember: boolean
  isKol: boolean
  isVendor: boolean
  isMemberProfile: boolean
  memberIdToQuery: string
  kolInfo?: KolType
  roleInfo?: KolType | MerchantType
  merchantInfo?: MerchantType
  isProfileTypeDefined: boolean
  refetchUserProfile: () => void
  refetchMerchantInfo: () => void
  refetchKOLInfo: () => void
  refetchFollowData: () => void
}
export const ProfileType = {
  VENDOR: 'vendor',
  KOL: 'kol',
  CLUB_MEMBER: 'clubMember',
  VIP_CLUB_MEMBER: 'vipClubMember',
} as const

export const useProfileData = (props?: IUserProfileDataParams): TProfileData => {
  const { memberData } = useMemberData()
  const { memberInfoId } = props || {}
  const router = useRouter()
  const { query } = router
  const memberIdToQuery = memberInfoId || query?.id?.[0] || memberData.memberInfoId

  const { merchantInfo, refetchMerchantInfo, kolInfo, refetchKOLInfo } = useMemberRoleInfo({ memberInfoId: memberIdToQuery })

  const { userProfile, refetchUserProfile, isSuccess: isUserProfileFetchSuccess } = useUserProfile({ memberInfoId: memberIdToQuery })
  const { followingMembers, refetchFollowingMembers } = useFollowingMembers({
    memberInfoId: memberIdToQuery,
    followerType: getLiveMallClubRole(userProfile?.roles),
    enabled: !!userProfile?.roles,
  })

  const hasMerchantRole = userProfile?.roles?.includes(ROLE_TYPE.MERCHANT)
  const hasKOLRole = userProfile?.roles?.includes(ROLE_TYPE.KOL)
  const { followers, refetchFollowers } = useFollowers({
    memberInfoId: hasMerchantRole ? merchantInfo?.id : memberIdToQuery,
    followeeType: getLiveMallClubRole(userProfile?.roles),
  })
  const getProfileType = () => {
    if (!isUserProfileFetchSuccess) {
      return null
    }
    switch (true) {
      case hasMerchantRole:
        return ProfileType.VENDOR
      case hasKOLRole:
        return ProfileType.KOL
      default:
        return ProfileType.CLUB_MEMBER
    }
  }
  const profileType = getProfileType()
  const isProfileTypeDefined = profileType !== null
  const isVendor = profileType === ProfileType.VENDOR
  const isClubMember = profileType === ProfileType.CLUB_MEMBER
  const isKol = profileType === ProfileType.KOL
  const isMemberProfile = profileType === ProfileType.CLUB_MEMBER

  const getCommonRoleInfo = () => {
    // 順序：賣家 > KOL > 會員
    if (isVendor) {
      return {
        name: merchantInfo?.storeName,
        intro: merchantInfo?.intro,
        coverPhotoUrl: merchantInfo?.coverPhoto,
      }
    }
    if (isKol) {
      return {
        name: userProfile?.nickName,
        intro: kolInfo?.intro,
        coverPhotoUrl: kolInfo?.coverPhoto,
      }
    }
    return {
      name: userProfile?.nickName,
      intro: userProfile?.intro,
    }
  }
  const { name, intro, coverPhotoUrl } = getCommonRoleInfo()

  // 是自己的 profile 就可以做一些編輯操作
  const isMyProfile = memberData?.memberInfoId === userProfile?.memberInfoId
  const refetchFollowData = () => {
    refetchFollowers()
    refetchFollowingMembers()
  }

  return {
    merchantId: userProfile?.roleDetail[ROLE_TYPE.MERCHANT]?.id,
    name,
    coverPhotoUrl,
    intro,
    headshotUrl: userProfile?.headshotUrl,
    roles: userProfile?.roles,
    userProfile,
    merchantInfo,
    kolInfo,
    isMyProfile,
    profileType,
    followingMembers,
    followers,
    isClubMember,
    isKol,
    isVendor,
    isMemberProfile,
    refetchUserProfile,
    refetchMerchantInfo,
    memberIdToQuery,
    refetchKOLInfo,
    roleInfo: isVendor ? merchantInfo : kolInfo,
    refetchFollowData,
    isProfileTypeDefined,
  }
}
